export type ConvertImageOptions = {
    compress?: boolean
    maxSize?: number | never
    format?: 'webp' | 'png' | 'jpeg'
}

const getDataSrc = async (data: string | Blob | ArrayBuffer | File): Promise<string> => {
    if (data instanceof File) {
        return getDataSrc(await data.arrayBuffer())
    } else if (data instanceof ArrayBuffer) {
        return getDataSrc(new Blob([data]))
    } else if (data instanceof Blob) {
        return getDataSrc(URL.createObjectURL(data))
    } else {
        return data
    }
}

export const convertImage = async (
    data: string | Blob | ArrayBuffer | File,
    {
        compress = false,
        maxSize,
        format = 'webp',
    }: {
        compress?: boolean
        maxSize?: number | never
        format?: 'webp' | 'png' | 'jpeg'
    }
) => {
    const src = await getDataSrc(data)

    // create the elements
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d') as CanvasRenderingContext2D
    const image = new Image()

    const blob = await new Promise<Blob>((resolve, reject) => {
        image.src = src
        image.crossOrigin = 'anonymous'
        image.onload = (e) => {
            if (e.target && e.target instanceof HTMLImageElement) {
                // revoke the object url since it already was loaded
                URL.revokeObjectURL(e.target.src)

                let newWidth = image.naturalWidth
                let newHeight = image.naturalHeight

                if (maxSize) {
                    const widthRatio = maxSize / image.naturalWidth
                    const heightRatio = maxSize / image.naturalHeight

                    if (widthRatio < 1 || heightRatio < 1) {
                        const ratio = Math.min(widthRatio, heightRatio)

                        newWidth = Math.floor(image.naturalWidth * ratio)
                        newHeight = Math.floor(image.naturalHeight * ratio)
                    }
                }

                canvas.width = newWidth
                canvas.height = newHeight
                context.drawImage(e.target, 0, 0, newWidth, newHeight)

                canvas.toBlob(
                    (data) => {
                        if (data) {
                            resolve(data as Blob)
                        } else {
                            reject(new Error('Cannot generate the image from canvas'))
                        }
                    },
                    `image/${format}`,
                    compress ? 0.8 : 1
                )
            } else {
                reject(new Error('Event target is null or is not an HTMLImageElement'))
            }
        }
        image.onerror = (e) => reject(e)
    })

    const result = {
        blob,
        width: canvas.width,
        height: canvas.height,
    }

    // remove the elements
    image.remove()
    canvas.remove()

    return result
}
